/**
 * Prefetching Utilities
 * 
 * Prefetch data for likely navigation targets to improve perceived performance.
 * Use these functions to preload data before the user navigates.
 */

import { QueryClient } from '@tanstack/react-query';
import companyService from '../services/companyService';
import agentService from '../services/agentService';
import customerService from '../services/customerService';
import productService from '../services/productService';

/**
 * Prefetch company list
 * Call this when user is on dashboard and might navigate to companies
 */
export const prefetchCompanies = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery({
    queryKey: ['companies', { skip: 0, limit: 50 }],
    queryFn: () => companyService.getCompanies(0, 50),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Prefetch agent list
 */
export const prefetchAgents = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery({
    queryKey: ['agents', { skip: 0, limit: 50 }],
    queryFn: () => agentService.getAgents(0, 50),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Prefetch customer list
 */
export const prefetchCustomers = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery({
    queryKey: ['customers', { skip: 0, limit: 50 }],
    queryFn: () => customerService.getCustomers(0, 50),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Prefetch product list
 */
export const prefetchProducts = async (queryClient: QueryClient) => {
  await queryClient.prefetchQuery({
    queryKey: ['products', { skip: 0, limit: 50 }],
    queryFn: () => productService.getProductsByCategory(0, 50),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Prefetch detail page for a specific item
 * Call this when user hovers over or clicks a "view details" button
 */
export const prefetchCompanyDetail = async (
  queryClient: QueryClient,
  companyId: string
) => {
  await queryClient.prefetchQuery({
    queryKey: ['company', companyId],
    queryFn: () => companyService.getCompany(companyId),
    staleTime: 2 * 60 * 1000, // 2 minutes (detail pages change less often)
  });
};

export const prefetchCustomerDetail = async (
  queryClient: QueryClient,
  customerId: string
) => {
  await queryClient.prefetchQuery({
    queryKey: ['customer', customerId],
    queryFn: () => customerService.getCustomer(customerId),
    staleTime: 2 * 60 * 1000,
  });
};

export const prefetchAgentDetail = async (
  queryClient: QueryClient,
  agentId: string
) => {
  await queryClient.prefetchQuery({
    queryKey: ['agent', agentId],
    queryFn: () => agentService.getAgent(agentId),
    staleTime: 2 * 60 * 1000,
  });
};

export const prefetchProductDetail = async (
  queryClient: QueryClient,
  productId: string
) => {
  await queryClient.prefetchQuery({
    queryKey: ['product', productId],
    queryFn: () => productService.getProduct(productId),
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Prefetch all lists from dashboard
 * Call this on dashboard load to preload all main lists
 */
export const prefetchDashboardLists = async (queryClient: QueryClient) => {
  // Prefetch with smaller limits to reduce initial load
  await Promise.all([
    prefetchCompanies(queryClient),
    prefetchAgents(queryClient),
    prefetchCustomers(queryClient),
    prefetchProducts(queryClient),
  ]);
};

/**
 * Smart prefetching: Only prefetch if not already in cache
 */
export const smartPrefetch = async <T>(
  queryClient: QueryClient,
  queryKey: string[],
  queryFn: () => Promise<T>,
  staleTime: number = 5 * 60 * 1000
) => {
  const cached = queryClient.getQueryData(queryKey);
  if (!cached) {
    await queryClient.prefetchQuery({
      queryKey,
      queryFn,
      staleTime,
    });
  }
};
